import React, { useState, useRef, useEffect, useMemo } from "react";
import { useAtom } from "jotai";
import {
    Grid,
    Typography,
    Autocomplete,
    IconButton,
    TextField,
    InputAdornment
} from "@mui/material";
import throttle from "lodash/throttle";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { tableConfigAtom } from "../atoms/bookingsTable";
import { useTheme, useMediaQuery } from "@mui/material";

export default function MBLookup(props) {
    const inputRef = useRef();
    const [value, setValue] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [tableConfig] = useAtom(tableConfigAtom);
    const { roleFilter } = tableConfig;
    const [noOptionsText, setNoOptionsText] = useState("Type to search");

    const fetch = useMemo(
        () =>
            throttle(async (request, callback) => {
                setLoading(true);
                let result = await props.searchService(request, roleFilter);
                callback(result);
                setLoading(false);
            }, 1000),
        [props, roleFilter]
    );
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"), {
        defaultMatches: true
    });

    useEffect(() => {
        inputRef.current.autocomplete = "off";
        props.setSelectedValue(value);
    }, []);

    useEffect(() => {
        let active = true;

        if (inputValue === "") {
            setOptions(value ? [value] : []);
            setNoOptionsText("Type to search");
            return;
        }

        if (inputReady(inputValue)) {
            setNoOptionsText("Loading...");
            fetch({ input: inputValue }, (results) => {
                if (active) {
                    let newOptions = [];

                    if (value) {
                        newOptions = [value];
                    }

                    if (results && results.length > 0) {
                        newOptions = [...newOptions, ...results];
                        setNoOptionsText("");
                    } else {
                        setNoOptionsText(`No ${props.userType} found`);
                    }

                    setOptions(newOptions);
                }
            });
        } else {
            setNoOptionsText("Type to search");
            setOptions([]);
        }

        return () => {
            active = false;
        };
    }, [inputValue, value, fetch]);

    function inputReady(inp) {
        return inp.trim().length > 2;
    }

    // Use setTimeout here to ensure dom is clearing blur after state is updated
    const handleBlurOnClear = () => {
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.blur();
            }
        }, 0);
    };

    const handleInputChange = (event, newInputValue, reason) => {
        if (reason === "clear") {
            setInputValue("");
            handleBlurOnClear();
        } else {
            setInputValue(newInputValue);
        }
    };

    const handleInputClear = () => {
        setValue(null);
        props.setSelectedValue(null);
        setInputValue("");
        setNoOptionsText("Type to search");
        handleBlurOnClear();
    };

    return (
        <Autocomplete
            id="user-lookup-autocomplete"
            getOptionLabel={(option) => option[props.headTextFieldName]}
            filterOptions={(x) => x}
            options={options}
            loadingText="Loading..."
            noOptionsText={noOptionsText}
            loading={loading}
            includeInputInList
            filterSelectedOptions
            openOnFocus
            size={!isDesktop ? "small" : ""}
            value={value}
            onChange={(event, newValue) => {
                setOptions(newValue ? [newValue, ...options] : options);
                setValue(newValue);
                props.setSelectedValue(newValue);
                inputRef.current.blur();
            }}
            onInputChange={handleInputChange}
            renderInput={(params) => (
                <TextField
                    {...params}
                    variant="outlined"
                    fullWidth
                    placeholder={props.placeholder}
                    inputRef={inputRef}
                    sx={{ fontSize: "14px" }}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: value && (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    aria-label="clear"
                                    onClick={handleInputClear}
                                >
                                    <ClearIcon
                                        sx={{
                                            width: "18px",
                                            height: "18px"
                                        }}
                                    />
                                </IconButton>
                            </InputAdornment>
                        ),
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon
                                    sx={{
                                        width: "18px",
                                        height: "18px"
                                    }}
                                />
                            </InputAdornment>
                        )
                    }}
                />
            )}
            renderOption={(propsOp, option) => {
                return (
                    <li {...propsOp}>
                        <Grid
                            key={option.id}
                            item
                            sx={{
                                display: "flex",
                                flexDirection: "column"
                            }}
                        >
                            <Typography
                                sx={{
                                    maxWidth: "218px",
                                    fontSize: "14px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                }}
                            >
                                {option[props.headTextFieldName]}
                            </Typography>
                            <Typography
                                sx={{
                                    maxWidth: "218px",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    fontSize: "0.75rem",
                                    fontWeight: "normal",
                                    color: theme.palette.helperText.main,
                                    fontStyle: "italic",
                                    fontFamily: ["Roboto", "sans-serif"].join(
                                        ","
                                    )
                                }}
                            >
                                {option[props.subTextFieldName]}
                            </Typography>
                        </Grid>
                    </li>
                );
            }}
            sx={{
                "& .MuiFormLabel-root": {
                    fontSize: "14px",
                    "&.Mui-focused": {
                        top: "2px"
                    },
                    "&.MuiInputLabel-shrink": {
                        top: "2px"
                    }
                },
                "& .MuiInputBase-root": {
                    display: "flex",
                    alignContent: "center",
                    padding: "7.95px 14px",
                    fontSize: "14px"
                },
                "& .MuiAutocomplete-inputRoot": {
                    height: "36px"
                },
                "& .MuiAutocomplete-option": {
                    fontSize: "14px"
                },
                "& .MuiAutocomplete-popupIndicator": {
                    marginRight: "-4px"
                }
            }}
        />
    );
}
